import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { gbp } from "../../lib/format";
import { formatApiError } from "../../lib/errors";
import { Modal, PrimaryButton, QuietButton, Select } from "../UI";

/** LT-38 — tick tender lines to compare a quote against. Phase / trade are pickers only; line ids are what is stored. */
export function CompareLinesDialog({ open, onClose, tenderId, initialIds, onSave }) {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(new Set());
  const [trade, setTrade] = useState("");
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSel(new Set(initialIds || [])); setTrade(""); setQ("");
    api.get(`/tenders/${tenderId}/subby-quotes/compare-lines`).then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e, "Failed to load tender lines")));
  }, [open, tenderId, initialIds]);

  const visible = (ln) => (!trade || ln.trade_name === trade) && (!q || (ln.description || "").toLowerCase().includes(q.toLowerCase()) || ln.ref.includes(q));
  const toggle = (id) => setSel((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const togglePhase = (lines) => setSel((s) => {
    const n = new Set(s); const ids = lines.filter(visible).map((l) => l.line_id);
    const all = ids.every((id) => n.has(id));
    ids.forEach((id) => (all ? n.delete(id) : n.add(id)));
    return n;
  });
  const tradeOptions = useMemo(() => [{ value: "", label: "All trades" }, ...((data?.trades || []).map((t) => ({ value: t, label: t })))], [data]);
  const selectedTotals = useMemo(() => {
    let lab = 0, mat = 0;
    (data?.phases || []).forEach((p) => p.lines.forEach((l) => { if (sel.has(l.line_id)) { lab += l.labour_total || 0; mat += l.material_total || 0; } }));
    return { lab, mat };
  }, [data, sel]);

  const save = async () => {
    setSaving(true);
    try { await onSave([...sel]); onClose(); }
    catch (e) { toast.error(formatApiError(e, "Could not save the selection")); }
    finally { setSaving(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title="Compare against tender lines" testid="compare-lines-dialog" maxWidth={760}
      footer={<>
        <span className="mr-auto text-ink-3" style={{ fontSize: 12 }} data-testid="compare-lines-summary">
          {sel.size} line{sel.size === 1 ? "" : "s"} · labour <span className="font-mono tnum">{gbp(selectedTotals.lab)}</span> · materials <span className="font-mono tnum">{gbp(selectedTotals.mat)}</span>
        </span>
        <QuietButton onClick={onClose} data-testid="compare-lines-cancel">Cancel</QuietButton>
        <PrimaryButton onClick={save} disabled={saving} data-testid="compare-lines-save">Save selection</PrimaryButton>
      </>}>
      <div className="flex items-center gap-3 mb-3">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search ref or description…" data-testid="compare-lines-search"
          className="flex-1 h-9 px-3 rounded-md border border-line bg-surface text-ink focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent" style={{ fontSize: 13 }} />
        <div style={{ width: 200 }} data-testid="compare-lines-trade"><Select value={trade} onChange={setTrade} options={tradeOptions} /></div>
      </div>
      <div className="max-h-[50vh] overflow-auto rounded-md border border-line divide-y divide-line-soft" data-testid="compare-lines-list">
        {!data ? <div className="p-6 text-ink-3" style={{ fontSize: 12 }}>Loading…</div>
        : data.line_count === 0 ? <div className="p-6 text-ink-3" style={{ fontSize: 12 }}>This tender has no lines yet.</div>
        : data.phases.map((p) => {
          const lines = p.lines.filter(visible);
          if (!lines.length) return null;
          const allOn = lines.every((l) => sel.has(l.line_id));
          return (
            <div key={p.phase_id}>
              <label className="flex items-center gap-2 px-3 py-2 bg-surface-2/60 cursor-pointer" data-testid={`compare-phase-${p.phase_id}`}>
                <input type="checkbox" checked={allOn} onChange={() => togglePhase(p.lines)} data-testid={`compare-phase-${p.phase_id}-all`} />
                <span className="font-mono uppercase text-ink-2" style={{ fontSize: 10.5, letterSpacing: "0.06em", fontWeight: 700 }}>{p.phase_name}</span>
                <span className="text-ink-3" style={{ fontSize: 11 }}>({lines.length})</span>
              </label>
              {lines.map((l) => (
                <label key={l.line_id} className="flex items-center gap-3 px-3 py-1.5 hover:bg-surface-2/40 cursor-pointer" data-testid={`compare-line-${l.line_id}`}>
                  <input type="checkbox" checked={sel.has(l.line_id)} onChange={() => toggle(l.line_id)} data-testid={`compare-line-${l.line_id}-check`} />
                  <span className="font-mono tnum text-ink-3" style={{ fontSize: 11, width: 40 }}>{l.ref}</span>
                  <span className="flex-1 truncate text-ink" style={{ fontSize: 12.5 }}>{l.description || <span className="text-ink-4">(no description)</span>}{l.sub_phase_name && <span className="text-ink-3"> · {l.sub_phase_name}</span>}</span>
                  {l.trade_name && <span className="rounded-full px-2 py-0.5 bg-surface-2 text-ink-2 border border-line" style={{ fontSize: 10.5 }}>{l.trade_name}</span>}
                  <span className="font-mono tnum text-ink-2 text-right" style={{ fontSize: 11.5, width: 84 }} title="Labour">{gbp(l.labour_total)}</span>
                  <span className="font-mono tnum text-ink-3 text-right" style={{ fontSize: 11.5, width: 84 }} title="Materials">{gbp(l.material_total)}</span>
                </label>
              ))}
            </div>
          );
        })}
      </div>
      <div className="mt-2 text-ink-4" style={{ fontSize: 11 }}>Columns: labour · materials. Plant and the recipe subby stream are not part of the comparison.</div>
    </Modal>
  );
}
